import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { GetRequest, PatchRequest } from "../../../api/Request";
import {
  SalesActivityEntry,
  SalesAssignee,
  SalesBillPoint,
  SalesStoreDetailResponse,
} from "./sales.types";
import { SalesActivityTypeEnum, SalesStatusEnum } from "../../../enum";
import { activityRecencyStripeColor, salesStatusMeta, salesStatusOptionList } from "./salesStatus.config";
import { formatDate, formatRelativeTime, formatTime, getActivityRecencyTier, isRecentlyCreated } from "../../../util/util";
import "./styles/salesDetail.scss";

type BillsGroupBy = "day" | "week";

const chartGroupByOptionList: { value: BillsGroupBy; label: string }[] = [
  { value: "day", label: "By Day" },
  { value: "week", label: "By Week" },
];

const billsChartHeading = "Bills Generated (Last 30 Days)";

const SalesDetail: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<SalesStoreDetailResponse | null>(null);
  const [assigneeList, setAssigneeList] = useState<SalesAssignee[]>([]);
  const [billPoints, setBillPoints] = useState<SalesBillPoint[]>([]);
  const [groupBy, setGroupBy] = useState<BillsGroupBy>("day");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [commentText, setCommentText] = useState("");
  const [publishingComment, setPublishingComment] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!storeId) return;
    await GetRequest(`admin/v1/sales/${storeId}`)
      .then((res: any) => setDetail(res.data))
      .catch((err) => setError(err.message || "Failed to fetch store details"))
      .finally(() => setLoading(false));
  }, [storeId]);

  const fetchBills = useCallback(async () => {
    if (!storeId) return;
    const res = await GetRequest(`admin/v1/sales/${storeId}/bills?groupBy=${groupBy}`);
    setBillPoints(res.data);
  }, [storeId, groupBy]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    fetchBills();
  }, [fetchBills]);

  useEffect(() => {
    GetRequest("admin/v1/sales/assignee").then((res) => setAssigneeList(res.data));
  }, []);

  const assigneeNameById = useMemo(() => {
    const map = new Map<string, string>();
    assigneeList.forEach((assignee) => map.set(assignee._id, assignee.name || "Team member"));
    return map;
  }, [assigneeList]);

  const assigneeLabel = (userId: string | null | undefined) =>
    userId ? assigneeNameById.get(userId) || "Team member" : "Unassigned";

  const statusLabel = (status: string | null | undefined) =>
    (status && salesStatusMeta[status as SalesStatusEnum]?.label) || status || "Unknown";

  const assignmentEntriesAsc = useMemo(() => {
    const activityLog = detail?.activityLog ?? [];
    return activityLog
      .filter((entry) => entry.type === SalesActivityTypeEnum.Assignment)
      .slice()
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [detail]);

  const assignmentSpanLabel = (entry: SalesActivityEntry): string => {
    const index = assignmentEntriesAsc.findIndex((e) => e.createdAt === entry.createdAt);
    const next = assignmentEntriesAsc[index + 1];
    const startMs = new Date(entry.createdAt).getTime();
    const endMs = next ? new Date(next.createdAt).getTime() : Date.now();
    const days = Math.max(0, Math.round((endMs - startMs) / 86400000));
    const durationLabel = days === 0 ? "same day" : `${days}d`;
    return next ? durationLabel : `${durationLabel} · current`;
  };

  const renderActivityLine = (entry: SalesActivityEntry) => {
    if (entry.type === SalesActivityTypeEnum.StatusChange) {
      return `Status changed: ${statusLabel(entry.previousValue)} → ${statusLabel(entry.newValue)}`;
    }
    if (entry.type === SalesActivityTypeEnum.Assignment) {
      return `Assigned: ${assigneeLabel(entry.previousValue)} → ${assigneeLabel(entry.newValue)} (${assignmentSpanLabel(entry)})`;
    }
    return entry.text;
  };

  const handleStatusChange = async (salesStatus: SalesStatusEnum) => {
    if (!storeId || !detail) return;
    setDetail({ ...detail, store: { ...detail.store, salesStatus } });
    await PatchRequest(`admin/v1/sales/${storeId}/status`, { salesStatus });
    fetchDetail();
  };

  const handleAssignChange = async (assignedTo: string) => {
    if (!storeId || !detail) return;
    setDetail({
      ...detail,
      store: {
        ...detail.store,
        assignedTo: assignedTo || null,
        assignedToName: assigneeNameById.get(assignedTo) || null,
      },
    });
    await PatchRequest(`admin/v1/sales/${storeId}/status`, { assignedTo: assignedTo || null });
    fetchDetail();
  };

  const handlePublishComment = async () => {
    if (!storeId || !commentText.trim()) return;
    setPublishingComment(true);
    await PatchRequest(`admin/v1/sales/${storeId}/status`, { comment: commentText.trim() });
    setCommentText("");
    setPublishingComment(false);
    fetchDetail();
  };

  if (loading) {
    return (
      <div className="salesDetail">
        <div className="salesDetailLoading">Loading store details...</div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="salesDetail">
        <div className="salesDetailError">{error || "Store not found"}</div>
      </div>
    );
  }

  const { store, activity } = detail;
  const activityLog = detail.activityLog ?? [];

  return (
    <div className="salesDetail">
      <div className="salesDetailHeader">
        <button className="backButton" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div>
          <h1>
            {store.name}
            {isRecentlyCreated(store.createdAt, 3) && <span className="newBadge">New</span>}
          </h1>
          <div className="storeId">Store ID: {store._id}</div>
        </div>
      </div>

      <div className="salesDetailContent">
        <div className="salesDetailCard">
          <h2>Store Information</h2>
          <div className="infoGrid">
            <div className="infoItem">
              <div className="label">Owner</div>
              <div className="value">{store.ownerName || "N/A"}</div>
            </div>
            <div className="infoItem">
              <div className="label">Phone</div>
              <div className="value">{store.phoneNumberList?.[0]?.phoneNumber || "N/A"}</div>
            </div>
            <div className="infoItem">
              <div className="label">Location</div>
              <div className="value">
                {[store.address?.city, store.address?.state].filter(Boolean).join(", ") || "N/A"}
              </div>
            </div>
            <div className="infoItem">
              <div className="label">Store Since</div>
              <div className="value">{store.createdAt ? formatDate(store.createdAt) : "N/A"}</div>
              {store.createdAt && <div className="createdTime">{formatTime(store.createdAt)}</div>}
            </div>
          </div>
        </div>

        <div className="salesDetailCard">
          <h2>Sales Status</h2>
          <div className="statusAssignRow">
            <div className="filterGroup">
              <label>Status</label>
              <select
                value={store.salesStatus}
                onChange={(e) => handleStatusChange(e.target.value as SalesStatusEnum)}
              >
                {salesStatusOptionList.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="filterGroup">
              <label>Assigned To</label>
              <select value={store.assignedTo || ""} onChange={(e) => handleAssignChange(e.target.value)}>
                <option value="">Unassigned</option>
                {assigneeList.map((assignee) => (
                  <option key={assignee._id} value={assignee._id}>
                    {assignee.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="infoItem">
            <div className="label">Last Contacted</div>
            <div className="value">
              {store.lastContactedAt ? formatRelativeTime(store.lastContactedAt) : "Never"}
            </div>
          </div>
        </div>

        <div className="salesDetailCard salesDetailCard--full">
          <h2>Activity</h2>
          <div className="metricsGrid">
            <div className="metric">
              <div className="metricLabel">Last Active</div>
              <div className="metricValue" style={{ fontSize: 16 }}>
                <span
                  className="activityDot"
                  style={{
                    background: activityRecencyStripeColor[getActivityRecencyTier(activity.lastActiveDate)],
                  }}
                />
                {activity.lastActiveDate ? formatRelativeTime(activity.lastActiveDate) : "Never"}
              </div>
            </div>
            <div className="metric">
              <div className="metricLabel">Total Active Days</div>
              <div className="metricValue">{activity.totalActiveDays}</div>
            </div>
            <div className="metric">
              <div className="metricLabel">Total Bills</div>
              <div className="metricValue">{activity.totalBills}</div>
            </div>
          </div>
        </div>

        <div className="salesDetailCard salesDetailCard--full">
          <h2>{billsChartHeading}</h2>
          <div className="chartToggle">
            {chartGroupByOptionList.map((option) => (
              <button
                key={option.value}
                className={groupBy === option.value ? "active" : ""}
                onClick={() => setGroupBy(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={billPoints}>
              <defs>
                <linearGradient id="billsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4a90e2" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#4a90e2" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip formatter={(value: number) => [`${value} bills`, "Bills"]} />
              <Area
                type="monotone"
                dataKey="totalBills"
                stroke="#4a90e2"
                fill="url(#billsGradient)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="salesDetailCard salesDetailCard--full">
          <h2>Activity</h2>
          <div className="commentForm">
            <textarea
              placeholder="Add a comment about this store..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
            />
            <button
              disabled={!commentText.trim() || publishingComment}
              onClick={handlePublishComment}
            >
              Publish
            </button>
          </div>
          <div className="commentTimeline">
            {activityLog.length === 0 ? (
              <div className="commentEmpty">No activity yet.</div>
            ) : (
              [...activityLog]
                .reverse()
                .map((entry, index) => (
                  <div className="commentEntry" key={index}>
                    <div className="commentText">{renderActivityLine(entry)}</div>
                    <div className="commentMeta">
                      {entry.authorName} · {formatRelativeTime(entry.createdAt)} ·{" "}
                      {formatTime(entry.createdAt)}
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesDetail;
